import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

export type SlotView =
  | { kind: "top" }
  | { kind: "all" }
  | { kind: "day"; date: string };

/**
 * Filtre de slots persisté dans l'URL via `?view=top|all|day:YYYY-MM-DD`.
 * Source unique pour la page Résultats — utilisé par les chips desktop et le
 * day pager mobile.
 */
export function useSlotFilter(defaultView: SlotView): {
  view: SlotView;
  setView: (next: SlotView) => void;
  toParam: (v: SlotView) => string;
} {
  const [params, setParams] = useSearchParams();
  const raw = params.get("view");

  const view = useMemo<SlotView>(() => {
    if (!raw) return defaultView;
    if (raw === "top") return { kind: "top" };
    if (raw === "all") return { kind: "all" };
    if (raw.startsWith("day:")) {
      const date = raw.slice(4);
      if (/^\d{4}-\d{2}-\d{2}$/.test(date)) return { kind: "day", date };
    }
    return defaultView;
  }, [raw, defaultView]);

  const toParam = useCallback((v: SlotView): string => {
    if (v.kind === "day") return `day:${v.date}`;
    return v.kind;
  }, []);

  const setView = useCallback(
    (next: SlotView) => {
      const p = new URLSearchParams(params);
      p.set("view", toParam(next));
      setParams(p, { replace: true });
    },
    [params, setParams, toParam],
  );

  return { view, setView, toParam };
}
